import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, MapPin, Car, BatteryCharging, CheckCircle, XCircle, Clock, FileDown, ArrowLeft, Calendar as CalendarIcon, AlertTriangle, Eye, EyeOff, LogOut } from 'lucide-react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { auth } from './firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';

// --- API Configuration ---
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api'                                 // Same domain as IIS
  : 'http://localhost:5001/api';           // Local backend on port 5001

  console.log("API Base URL:", process.env.NODE_ENV);
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// API Token now handled by backend - no longer exposed to client!

// Helper function to get authenticated headers with Firebase token
const getAuthHeaders = async (user) => {
  console.log('🔑 getAuthHeaders called with user:', user ? user.email : 'NO USER');
  
  const headers = {
    'Content-Type': 'application/json'
  };
  
  if (user) {
    try {
      const token = await user.getIdToken();
      console.log('✅ Got Firebase token:', token.substring(0, 20) + '...');
      headers['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      console.error('❌ Error getting Firebase token:', error);
    }
  } else {
    console.warn('⚠️ No user provided to getAuthHeaders');
  }
  
  console.log('📤 Headers to send:', { ...headers, Authorization: headers.Authorization ? 'Bearer [TOKEN]' : 'NONE' });
  return headers;
};

// Mocked data for chargers, as there is no API endpoint for it.
const MOCKED_CHARGERS = [
    { id: "C001", name: "בית אדוויס", location_geo: { lat: 32.16165, lng: 34.93400 }, address: "עתיר ידע 16, כפר סבא" },
    { id: "C002", name: "קניון G", location_geo: { lat: 32.16000, lng: 34.93000 }, address: "התע\"ש 22, כפר סבא" },
    { id: "C003", name: "חניון עירוני", location_geo: { lat: 32.06300, lng: 34.77200 }, address: "דרך מנחם בגין 1, תל אביב" },
];

// Helper function to calculate distance between two lat/lng points in meters
const getDistanceInMeters = (lat1, lon1, lat2, lon2) => {
  if (lat1 === null || lon1 === null || lat2 === null || lon2 === null) return Infinity;
  const R = 6371e3; // metres
  const φ1 = lat1 * Math.PI/180;
  const φ2 = lat2 * Math.PI/180;
  const Δφ = (lat2-lat1) * Math.PI/180;
  const Δλ = (lon2-lon1) * Math.PI/180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c; // in metres
}

// Firebase Login Component
const FirebaseLoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Login error:', error);
      switch (error.code) {
        case 'auth/invalid-email':
          setError('כתובת אימייל לא תקינה');
          break;
        case 'auth/user-disabled':
          setError('המשתמש חסום');
          break;
        case 'auth/user-not-found':
          setError('משתמש לא נמצא');
          break;
        case 'auth/wrong-password':
          setError('סיסמה שגויה');
          break;
        case 'auth/invalid-credential':
          setError('פרטי התחברות שגויים');
          break;
        default:
          setError('שגיאה בהתחברות: ' + error.message);
      }
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4" dir="rtl">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-24 w-24 bg-blue-600 rounded-full flex items-center justify-center mb-4">
            <Car className="h-12 w-12 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">סימולטור דלקן חכם</h2>
          <p className="text-gray-600">התחבר למערכת בדיקת סמיכות רכבים</p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="bg-white p-8 rounded-xl shadow-lg space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                כתובת אימייל
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="הכנס כתובת אימייל"
                disabled={isLoading}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                סיסמה
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pl-10"
                  placeholder="הכנס סיסמה"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 left-0 pl-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Eye className="h-5 w-5 text-gray-400" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                <AlertTriangle className="w-4 h-4 inline ml-2" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
                  מתחבר...
                </div>
              ) : (
                'התחבר למערכת'
              )}
            </button>
          </div>
        </form>

        <div className="text-center">
          <p className="text-sm text-gray-500">
            גישה למשתמשים מורשים בלבד
          </p>
        </div>
      </div>
    </div>
  );
};

// Main Application Component
export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('proximity'); // 'proximity' or 'history'
  const [cars, setCars] = useState([]);
  const [chargers, setChargers] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null); // Single car for proximity test
  const [selectedCarsHistory, setSelectedCarsHistory] = useState([]); // Multi-select for history
  const [selectedCharger, setSelectedCharger] = useState(null);
  const [radius, setRadius] = useState(50);
  const [checkResult, setCheckResult] = useState(null); // Single result for proximity
  const [carDetails, setCarDetails] = useState(null); // Single car details
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Firebase Auth State Listener
  useEffect(() => {
    console.log('🔐 Setting up Firebase auth listener...');
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('🔄 Auth state changed:', user ? `Logged in as ${user.email}` : 'NOT LOGGED IN');
      setUser(user);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load cars when user is authenticated
  useEffect(() => {
    console.log('🚗 useEffect: fetchCars triggered, user:', user ? user.email : 'NO USER');
    if (!user) {
      console.warn('⚠️ User not authenticated, skipping car fetch');
      return;
    }

    const fetchCars = async () => {
        console.log('📡 Starting fetchCars for user:', user.email);
        setIsLoading(true);
        setApiError(null);
        try {
            const headers = await getAuthHeaders(user);
            console.log('📤 Fetching cars list with headers:', headers);
            
            const listResponse = await fetch(`${API_BASE_URL}/cars/list`, {
                headers
            });
            if (!listResponse.ok) {
                if(listResponse.status === 401) throw new Error('טוקן API אינו חוקי או שפג תוקפו.');
                if(listResponse.status === 403) throw new Error('אין לך הרשאה לצפות ברכבים אלה.');
                throw new Error(`שגיאת רשת בטעינת רשימת הרכבים! סטטוס: ${listResponse.status}`);
            }
            const listData = await listResponse.json();

            if (listData.success && Array.isArray(listData.data)) {
                const carNumbers = listData.data;
                if (carNumbers.length === 0) {
                    setCars([]);
                    return;
                }
                
                const infoResponse = await fetch(`${API_BASE_URL}/cars/info`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ car_numbers: carNumbers })
                });
                if (!infoResponse.ok) {
                    if(infoResponse.status === 403) throw new Error('אין לך הרשאה לצפות ברכבים אלה.');
                    throw new Error(`שגיאת רשת בטעינת פרטי הרכבים! סטטוס: ${infoResponse.status}`);
                }
                const infoData = await infoResponse.json();

                if (infoData.success) {
                    setCars(infoData.data);
                } else {
                    throw new Error('ה-API החזיר שגיאה בטעינת פרטי הרכבים.');
                }
            } else {
                throw new Error('פורמט נתונים לא תקין מרשימת הרכבים.');
            }
        } catch (error) {
            console.error('Error fetching car list:', error);
            setApiError(error.message || 'שגיאה בטעינת רשימת הרכבים. אנא נסה שוב מאוחר יותר.');
        } finally {
            setIsLoading(false);
        }
    };
    
    fetchCars();
    setChargers(MOCKED_CHARGERS);
  }, [user]);
  
  // Handlers
  const handleCarSelect = (carNumber) => {
    setSelectedCar(carNumber);
    // Clear existing result when selection changes
    setCheckResult(null);
    setCarDetails(null);
  };

  const handleCarsHistorySelect = (carNumbers) => {
    setSelectedCarsHistory(carNumbers);
  };

  const handleChargerSelect = (chargerId) => {
    const charger = chargers.find(c => c.id === chargerId);
    setSelectedCharger(charger);
    setCheckResult(null);
  };

  const handleCheck = async () => {
    if (!selectedCar || !selectedCharger) {
        console.warn("Please select a car and a charger.");
        return;
    }
    setIsChecking(true);
    setCheckResult(null);
    setApiError(null);

    try {
        const headers = await getAuthHeaders(user);
        
        const response = await fetch(`${API_BASE_URL}/cars/info`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ car_numbers: [selectedCar] })
        });
        if (!response.ok) {
            if(response.status === 403) throw new Error('אין לך הרשאה לצפות ברכב זה.');
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        if (data.success && data.data.length > 0) {
            const carInfo = data.data[0];
            setCarDetails(carInfo);
            
            if (carInfo.lat != null && carInfo.lng != null && selectedCharger.location_geo) {
                const distance = getDistanceInMeters(
                    carInfo.lat, 
                    carInfo.lng, 
                    selectedCharger.location_geo.lat, 
                    selectedCharger.location_geo.lng
                );
                setCheckResult({
                    carNumber: carInfo.car_number,
                    status: distance <= radius ? 'approved' : 'denied',
                    distance: Math.round(distance)
                });
            } else {
                setCheckResult({
                    carNumber: carInfo.car_number,
                    status: 'error',
                    message: 'לא התקבל מיקום עבור הרכב או המטען.'
                });
            }
        } else {
             throw new Error('API returned success: false or no data for the selected car.');
        }
    } catch (error) {
        console.error('Error fetching car info:', error);
        setCheckResult({
            carNumber: selectedCar,
            status: 'error',
            message: 'לא ניתן היה לקבל את מיקום הרכב.'
        });
    } finally {
        setIsChecking(false);
    }
  };

  const navigateToHistory = () => {
      setCurrentPage('history');
  };

  const navigateToProximity = () => {
      setCurrentPage('proximity');
      setCheckResult(null);
      setCarDetails(null);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      // Clear all data when user logs out
      setCars([]);
      setSelectedCar(null);
      setSelectedCarsHistory([]);
      setSelectedCharger(null);
      setCheckResult(null);
      setCarDetails(null);
      setCurrentPage('proximity');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Show loading screen while checking auth state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">טוען מערכת...</p>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!user) {
    return <FirebaseLoginScreen />;
  }
  
  // Render logic
  return (
    <div className="bg-gray-50 min-h-screen font-sans text-gray-800" dir="rtl">
      <Header user={user} onLogout={handleLogout} currentPage={currentPage} onNavigate={(page) => setCurrentPage(page)} />
      <main className="p-4 sm:p-6 md:p-8 max-w-7xl mx-auto">
        {isLoading ? (
            <div className="text-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">טוען נתונים...</p>
            </div>
        ) : (
            <>
                {apiError && !isLoading && <ApiError message={apiError} />}
                {currentPage === 'proximity' && (
                  <ProximityDashboard
                    cars={cars}
                    chargers={chargers}
                    selectedCar={selectedCar}
                    selectedCharger={selectedCharger}
                    radius={radius}
                    checkResult={checkResult}
                    carDetails={carDetails}
                    isChecking={isChecking}
                    onCarSelect={handleCarSelect}
                    onChargerSelect={handleChargerSelect}
                    onRadiusChange={setRadius}
                    onCheck={handleCheck}
                  />
                )}
                {currentPage === 'history' && (
                  <HistoryScreen
                    allCars={cars}
                    selectedCars={selectedCarsHistory}
                    onCarSelect={handleCarsHistorySelect}
                    onBack={navigateToProximity}
                    radius={radius}
                    user={user}
                  />
                )}
            </>
        )}
      </main>
    </div>
  );
}

// Header Component with Tabs
const Header = ({ user, onLogout, currentPage, onNavigate }) => (
  <header className="bg-white shadow-md border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="py-4 flex justify-between items-center">
        <div className="flex items-center space-x-4 space-x-reverse">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white font-bold text-2xl rounded-lg p-3 shadow-lg">
            A
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">ADVICE</h1>
            <p className="text-sm text-gray-500">מערכת ניהול טעינה</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-4 space-x-reverse">
          <div className="text-right hidden sm:block">
            <p className="text-xs text-gray-500">מחובר כ:</p>
            <p className="text-sm font-medium text-gray-800">{user?.email}</p>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center space-x-2 space-x-reverse bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-all shadow-md hover:shadow-lg"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">התנתק</span>
          </button>
        </div>
      </div>
      
      {/* Tab Navigation */}
      <div className="flex space-x-2 space-x-reverse border-t border-gray-200 pt-2">
        <button
          onClick={() => onNavigate('proximity')}
          className={`flex items-center space-x-2 space-x-reverse px-6 py-3 rounded-t-lg font-medium transition-all ${
            currentPage === 'proximity'
              ? 'bg-blue-500 text-white shadow-md'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <BatteryCharging className="w-5 h-5" />
          <span>דלקן חכם</span>
        </button>
        <button
          onClick={() => onNavigate('history')}
          className={`flex items-center space-x-2 space-x-reverse px-6 py-3 rounded-t-lg font-medium transition-all ${
            currentPage === 'history'
              ? 'bg-blue-500 text-white shadow-md'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Clock className="w-5 h-5" />
          <span>היסטוריית טעינות</span>
        </button>
      </div>
    </div>
  </header>
);

// Proximity Dashboard Component (renamed from Dashboard)
const ProximityDashboard = ({ cars, chargers, selectedCar, selectedCharger, radius, checkResult, carDetails, isChecking, onCarSelect, onChargerSelect, onRadiusChange, onCheck }) => {
  return (
    <div className="space-y-6">
      {/* Control Panel Card */}
      <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <BatteryCharging className="w-7 h-7 ml-3 text-blue-500" />
          אימות סמיכות רכב למטען
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Single-Select for Car */}
          <div>
            <CustomSelect 
              label="בחר רכב" 
              icon={<Car className="w-5 h-5 text-blue-500"/>} 
              options={cars.map(car => ({ 
                value: car.car_number, 
                label: `${car.brand || ''} ${car.model || 'רכב'} (${car.car_number})` 
              }))}
              value={selectedCar}
              onChange={(e) => onCarSelect(e.target.value)}
              placeholder="בחר רכב מהרשימה"
              disabled={cars.length === 0}
            />
          </div>
          
          {/* Charger Selection */}
          <div>
            <CustomSelect 
              label="בחר עמדת טעינה" 
              icon={<BatteryCharging className="w-5 h-5 text-amber-500"/>}
              options={chargers.map(ch => ({ value: ch.id, label: `${ch.name} - ${ch.address}` }))}
              value={selectedCharger?.id}
              onChange={(e) => onChargerSelect(e.target.value)}
              placeholder="בחר מרשימת העמדות"
            />
          </div>
        </div>
        
        {/* Radius Control */}
        <div className="mt-6">
          <label htmlFor="radius" className="block text-base font-semibold text-gray-700 mb-3">
            רדיוס אימות: <span className="text-blue-600 text-xl">{radius} מ'</span>
          </label>
          <input 
            type="range" 
            id="radius" 
            min="10" 
            max="8000" 
            step="50" 
            value={radius} 
            onChange={(e) => onRadiusChange(Number(e.target.value))} 
            className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
          />
          <div className="flex justify-between text-sm text-gray-500 mt-2">
            <span>10 מ'</span>
            <span>8000 מ'</span>
          </div>
        </div>
        
        {/* Action Button */}
        <div className="mt-8">
          <button 
            onClick={onCheck} 
            disabled={!selectedCar || !selectedCharger || isChecking} 
            className="w-full flex justify-center items-center bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-300"
          >
            {isChecking ? (
              <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <>
                <CheckCircle className="w-6 h-6 ml-2" />
                ודא סמיכות
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Result Section */}
      {checkResult && (
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-gray-800">תוצאת אימות</h3>
          <ResultCard 
            result={checkResult} 
            carInfo={cars.find(c => c.car_number === checkResult.carNumber)}
            radius={radius}
          />
        </div>
      )}
      
      {/* Map Display */}
      {selectedCharger && carDetails && (
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <MapPin className="w-6 h-6 ml-2 text-blue-500" />
            מפת מיקומים
          </h2>
          <MapDisplay carDetails={carDetails} charger={selectedCharger} radius={radius} />
        </div>
      )}
    </div>
  );
};

// Custom Select Component
const CustomSelect = ({ label, icon, options, value, onChange, placeholder, disabled }) => (
  <div>
    <label className="block text-base font-semibold text-gray-700 mb-2">{label}</label>
    <div className="relative">
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        {icon}
      </div>
      <select 
        value={value || ''}
        onChange={onChange}
        disabled={disabled}
        className="w-full block appearance-none bg-white border-2 border-gray-300 text-gray-700 py-3 pl-10 pr-12 rounded-xl leading-tight focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100 disabled:text-gray-400 transition-all shadow-sm hover:shadow-md"
      >
        <option value="" disabled>{placeholder}</option>
        {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center px-3 text-gray-700">
        <ChevronDown className="w-5 h-5" />
      </div>
    </div>
  </div>
);

// Multi-Select Component for Cars
const MultiSelectCars = ({ label, icon, cars, selectedCars, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const toggleCar = (carNumber) => {
    if (selectedCars.includes(carNumber)) {
      onChange(selectedCars.filter(c => c !== carNumber));
    } else {
      onChange([...selectedCars, carNumber]);
    }
  };
  
  const selectAll = () => {
    onChange(cars.map(c => c.car_number));
  };
  
  const clearAll = () => {
    onChange([]);
  };
  
  return (
    <div className="relative">
      <label className="block text-base font-semibold text-gray-700 mb-2">{label}</label>
      
      {/* Selected Display */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full flex items-center justify-between bg-white border-2 border-gray-300 text-gray-700 py-3 px-4 rounded-xl leading-tight focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100 disabled:text-gray-400 transition-all shadow-sm hover:shadow-md"
      >
        <div className="flex items-center flex-1">
          <div className="ml-2">{icon}</div>
          <span className={selectedCars.length === 0 ? "text-gray-400" : "text-gray-700"}>
            {selectedCars.length === 0 
              ? (cars.length === 0 ? "לא נמצאו רכבים" : "בחר רכבים מהרשימה")
              : `נבחרו ${selectedCars.length} רכבים`
            }
          </span>
        </div>
        <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>
      
      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 w-full mt-2 bg-white border-2 border-gray-300 rounded-xl shadow-2xl max-h-96 overflow-auto">
            {/* Quick Actions */}
            <div className="sticky top-0 bg-gray-50 border-b border-gray-200 p-3 flex justify-between items-center">
              <button
                type="button"
                onClick={selectAll}
                className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
              >
                בחר הכל
              </button>
              <button
                type="button"
                onClick={clearAll}
                className="text-sm text-gray-600 hover:text-gray-700 font-semibold"
              >
                נקה הכל
              </button>
            </div>
            
            {/* Car List */}
            <div className="p-2">
              {cars.map(car => {
                const isSelected = selectedCars.includes(car.car_number);
                return (
                  <button
                    key={car.car_number}
                    type="button"
                    onClick={() => toggleCar(car.car_number)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg mb-1 transition-all ${
                      isSelected 
                        ? 'bg-blue-50 border-2 border-blue-500' 
                        : 'bg-white hover:bg-gray-50 border-2 border-transparent'
                    }`}
                  >
                    <div className="flex items-center flex-1">
                      <div className={`w-5 h-5 rounded border-2 ml-3 flex items-center justify-center ${
                        isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                      }`}>
                        {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                      </div>
                      <span className="text-right">
                        {`${car.brand || ''} ${car.model || 'רכב'}`}
                        <span className="text-gray-500 text-sm mr-2">({car.car_number})</span>
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Result Card Component
const ResultCard = ({ result, carInfo, radius }) => {
  const isApproved = result.status === 'approved';
  const isDenied = result.status === 'denied';
  const isError = result.status === 'error';

  const bgColor = isApproved ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-300' : isDenied ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-300' : 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-300';
  const iconColor = isApproved ? 'text-green-600' : isDenied ? 'text-red-600' : 'text-yellow-600';
  const titleColor = isApproved ? 'text-green-800' : isDenied ? 'text-red-800' : 'text-yellow-800';
  const badgeColor = isApproved ? 'bg-green-500' : isDenied ? 'bg-red-500' : 'bg-yellow-500';

  const title = isApproved ? 'אושר ✓' : isDenied ? 'נדחה ✗' : 'שגיאה';
  let message = '';
  if (isApproved) message = `הרכב בטווח המותר: ${result.distance} מ'`;
  if (isDenied) message = `הרכב מחוץ לטווח: ${result.distance} מ' (מקסימום: ${radius} מ')`;
  if (isError) message = result.message || 'אירעה שגיאה לא צפויה.';

  return (
    <div className={`relative rounded-2xl shadow-lg border-2 p-6 transition-all hover:shadow-xl ${bgColor}`}>
      {/* Status Badge */}
      <div className={`absolute top-4 left-4 ${badgeColor} text-white text-xs font-bold px-3 py-1 rounded-full shadow-md`}>
        {title}
      </div>
      
      <div className="mt-8">
        <div className="flex items-start">
          <div className={`${iconColor} ml-3 mt-1`}>
            {isApproved && <CheckCircle className="w-10 h-10" />}
            {isDenied && <XCircle className="w-10 h-10" />}
            {isError && <AlertTriangle className="w-10 h-10" />}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-800 mb-1">
              {carInfo ? `${carInfo.brand || ''} ${carInfo.model || 'רכב'}` : 'רכב'}
            </h3>
            <p className="text-sm text-gray-600 mb-2">מס' רכב: {result.carNumber}</p>
            <p className={`text-sm font-medium ${titleColor}`}>{message}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Google Map Component
const GoogleMap = ({ carDetails, charger, radius }) => {
  const mapRef = React.useRef(null);
  const [map, setMap] = React.useState(null);
  const markersRef = React.useRef({ car: null, charger: null, radiusCircle: null });

  React.useEffect(() => {
    if (mapRef.current && !map) {
      // Center map on Israel (Tel Aviv area) as default
      const center = carDetails && charger 
        ? { 
            lat: (carDetails.lat + charger.location_geo.lat) / 2, 
            lng: (carDetails.lng + charger.location_geo.lng) / 2 
          }
        : { lat: 32.0853, lng: 34.7818 }; // Tel Aviv

      const googleMap = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: carDetails && charger ? 13 : 10,
        language: 'he',
        region: 'IL',
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
        styles: [
          {
            featureType: "all",
            elementType: "labels.text",
            stylers: [{ visibility: "on" }]
          }
        ]
      });

      setMap(googleMap);
    }
  }, [mapRef, map]);

  React.useEffect(() => {
    if (map && carDetails && charger) {
      // Clear existing markers
      if (markersRef.current.car) markersRef.current.car.setMap(null);
      if (markersRef.current.charger) markersRef.current.charger.setMap(null);
      if (markersRef.current.radiusCircle) markersRef.current.radiusCircle.setMap(null);

      // Create car marker
      const carMarker = new window.google.maps.Marker({
        position: { lat: carDetails.lat, lng: carDetails.lng },
        map: map,
        title: `רכב ${carDetails.car_number}`,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#3B82F6',
          fillOpacity: 1,
          strokeColor: '#1E40AF',
          strokeWeight: 3,
        }
      });

      const carInfoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="direction: rtl; font-family: Arial; min-width: 200px;">
            <h3 style="color: #1E40AF; margin: 0 0 10px 0;">🚗 רכב ${carDetails.car_number}</h3>
            <p style="margin: 5px 0;"><strong>מותג:</strong> ${carDetails.brand || 'לא זמין'}</p>
            <p style="margin: 5px 0;"><strong>דגם:</strong> ${carDetails.model || 'לא זמין'}</p>
            <p style="margin: 5px 0;"><strong>מיקום:</strong> ${carDetails.lat.toFixed(6)}, ${carDetails.lng.toFixed(6)}</p>
          </div>
        `
      });

      carMarker.addListener('click', () => {
        carInfoWindow.open(map, carMarker);
      });

      // Charger marker
      const chargerMarker = new window.google.maps.Marker({
        position: charger.location_geo,
        map: map,
        title: `${charger.name} - ${charger.address}`,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#F59E0B',
          fillOpacity: 1,
          strokeColor: '#D97706',
          strokeWeight: 3,
        }
      });

      // Radius circle
      const radiusCircle = new window.google.maps.Circle({
        strokeColor: '#10B981',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#10B981',
        fillOpacity: 0.15,
        map: map,
        center: charger.location_geo,
        radius: radius,
      });

      // Store references
      markersRef.current = { car: carMarker, charger: chargerMarker, radiusCircle: radiusCircle };

      const chargerInfoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="direction: rtl; font-family: Arial; min-width: 200px;">
            <h3 style="color: #D97706; margin: 0 0 10px 0;">🔌 ${charger.name}</h3>
            <p style="margin: 5px 0;"><strong>כתובת:</strong> ${charger.address}</p>
            <p style="margin: 5px 0;"><strong>מיקום:</strong> ${charger.location_geo.lat.toFixed(6)}, ${charger.location_geo.lng.toFixed(6)}</p>
            <p style="margin: 5px 0; color: #059669;"><strong>רדיוס אימות:</strong> ${radius} מטר</p>
          </div>
        `
      });

      chargerMarker.addListener('click', () => {
        chargerInfoWindow.open(map, chargerMarker);
      });

      // Auto-fit map to show all markers and radius
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend({ lat: carDetails.lat, lng: carDetails.lng });
      bounds.extend(charger.location_geo);
      
      // Extend bounds to include radius circle
      const radiusInDegrees = radius / 111320;
      bounds.extend({
        lat: charger.location_geo.lat + radiusInDegrees,
        lng: charger.location_geo.lng + radiusInDegrees
      });
      bounds.extend({
        lat: charger.location_geo.lat - radiusInDegrees,
        lng: charger.location_geo.lng - radiusInDegrees
      });
      
      map.fitBounds(bounds);

      const listener = window.google.maps.event.addListener(map, 'idle', () => {
        if (map.getZoom() > 18) map.setZoom(18);
        if (map.getZoom() < 8) map.setZoom(8);
        window.google.maps.event.removeListener(listener);
      });
    }
  }, [map, carDetails, charger, radius]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (markersRef.current.car) markersRef.current.car.setMap(null);
      if (markersRef.current.charger) markersRef.current.charger.setMap(null);
      if (markersRef.current.radiusCircle) markersRef.current.radiusCircle.setMap(null);
    };
  }, []);

  return <div ref={mapRef} style={{ width: '100%', height: '500px', borderRadius: '1rem' }} />;
};

// Map Display Component with Google Maps
const MapDisplay = ({ carDetails, charger, radius }) => {
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="w-full h-96 bg-yellow-50 border-2 border-yellow-200 rounded-xl flex items-center justify-center">
        <div className="text-center p-6">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">Google Maps API Key חסר</h3>
          <p className="text-yellow-700">אנא הוסף את REACT_APP_GOOGLE_MAPS_API_KEY לקובץ .env</p>
        </div>
      </div>
    );
  }

  const hasData = carDetails && charger;

  const render = (status) => {
    switch (status) {
      case Status.LOADING:
        return (
          <div className="w-full h-96 bg-gray-100 rounded-xl flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">טוען מפה...</p>
            </div>
          </div>
        );
      case Status.FAILURE:
        return (
          <div className="w-full h-96 bg-red-50 border-2 border-red-200 rounded-xl flex items-center justify-center">
            <div className="text-center p-6">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-800 mb-2">שגיאה בטעינת המפה</h3>
              <p className="text-red-700">בדוק את מפתח Google Maps API או חיבור האינטרנט</p>
            </div>
          </div>
        );
      default:
        return hasData ? (
          <GoogleMap carDetails={carDetails} charger={charger} radius={radius} />
        ) : (
          <div className="w-full h-96 bg-gray-100 rounded-xl flex items-center justify-center">
            <p className="text-gray-500">בחר רכב ומטען ולחץ "ודא סמיכות" להצגת מיקום במפה.</p>
          </div>
        );
    }
  };

  return (
    <Wrapper 
      apiKey={GOOGLE_MAPS_API_KEY} 
      render={render}
      libraries={['places']}
      language="he"
      region="IL"
    />
  );
};

// History Screen Component
const HistoryScreen = ({ allCars, selectedCars, onCarSelect, onBack, radius, user }) => {
  const [chargingHistory, setChargingHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [dateRange, setDateRange] = useState({
      from: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0],
      to: new Date().toISOString().split('T')[0]
  });

  const formatDateForApi = (dateString) => {
      if (!dateString) return null;
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    if (!selectedCars || selectedCars.length === 0) {
      setChargingHistory([]);
      return;
    }

    const fetchHistory = async () => {
        setIsLoading(true);
        setApiError(null);
        try {
            const headers = await getAuthHeaders(user);
            
            const response = await fetch(`${API_BASE_URL}/reports/cars/charging`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    car_numbers: selectedCars,
                    date_from: formatDateForApi(dateRange.from),
                    date_to: formatDateForApi(dateRange.to)
                })
            });
            if (!response.ok) {
                if(response.status === 403) throw new Error('אין לך הרשאה לצפות בהיסטוריה של רכבים אלה.');
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            if (data.success) {
                // Flatten array of arrays into single array with car_number added
                const allHistory = data.data.flatMap((carHistory, index) => 
                    carHistory.map(item => ({ ...item, car_number: selectedCars[index] }))
                );
                setChargingHistory(allHistory);
            } else {
                throw new Error('API returned success: false when fetching history.');
            }
        } catch (error) {
            console.error('Error fetching charging history:', error);
            setApiError('שגיאה בטעינת היסטוריית הטעינות.');
        } finally {
            setIsLoading(false);
        }
    };
    
    fetchHistory();
  }, [selectedCars, dateRange]);
  
  const exportToExcel = () => {
    const headers = ["מס' רכב", "תאריך התחלה", "תאריך סיום", "משך", "אחוז התחלה", "אחוז סיום", "סה\"כ אחוזים", "סה\"כ קוט\"ש", "עלות", "מיקום", "כתובת", "בעלים", "טלפון", "קילומטראז'", "מרחק (מ')"];
    const data = chargingHistory.map(item => `"${[item.car_number, item.datetime_start, item.datetime_end, item.duration, item.percents_start, item.percents_end, item.total_percents, item.total_kw, item.total_price, item.location_name, item.location_address, item.owner_name, item.owner_phone, item.odo, item.distance].join('","')}"`);
    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + [headers.join(','), ...data].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `charging_history_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
              <Clock className="w-7 h-7 ml-3 text-blue-500" />
              היסטוריית טעינות
            </h2>
            <p className="text-gray-500 mt-1">
              {selectedCars.length === 0 
                ? 'בחר רכבים לצפייה בהיסטוריה'
                : selectedCars.length === 1 
                  ? `רכב נבחר: ${allCars.find(c => c.car_number === selectedCars[0])?.brand || ''} ${allCars.find(c => c.car_number === selectedCars[0])?.model || ''} (${selectedCars[0]})`
                  : `${selectedCars.length} רכבים נבחרו`
              }
            </p>
          </div>
          <button 
            onClick={onBack} 
            className="flex items-center bg-gray-100 text-gray-700 font-semibold py-3 px-5 rounded-xl hover:bg-gray-200 transition-all shadow-md hover:shadow-lg"
          >
            <ArrowLeft className="w-5 h-5 ml-2" />
            חזרה
          </button>
        </div>
      </div>
      
      {/* Car Selection Card */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
        <MultiSelectCars 
          label="בחר רכבים לצפייה בהיסטוריה" 
          icon={<Car className="w-5 h-5 text-blue-500"/>} 
          cars={allCars}
          selectedCars={selectedCars}
          onChange={onCarSelect}
          disabled={allCars.length === 0}
        />
      </div>
      
      {/* Filters and Export Card */}
      {selectedCars.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div className="flex items-center gap-4 flex-wrap">
              <CalendarIcon className="w-6 h-6 text-blue-500"/>
              <div className="flex items-center gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">מתאריך</label>
                  <input 
                    type="date" 
                    value={dateRange.from} 
                    onChange={e => setDateRange({...dateRange, from: e.target.value})} 
                    className="border-2 border-gray-300 rounded-lg p-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                  />
                </div>
                <span className="text-gray-400 mt-6">—</span>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">עד תאריך</label>
                  <input 
                    type="date" 
                    value={dateRange.to} 
                    onChange={e => setDateRange({...dateRange, to: e.target.value})} 
                    className="border-2 border-gray-300 rounded-lg p-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                  />
                </div>
              </div>
            </div>
            <button 
              onClick={exportToExcel} 
              disabled={chargingHistory.length === 0} 
              className="flex items-center bg-gradient-to-r from-green-500 to-green-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl hover:from-green-600 hover:to-green-700 transition-all disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed"
            >
              <FileDown className="w-5 h-5 ml-2" />
              ייצוא ל-Excel
            </button>
          </div>
        </div>
      )}

      {/* Results Card */}
      {selectedCars.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-6">
            {isLoading && (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500 text-lg">טוען היסטוריה...</p>
              </div>
            )}
            
            {apiError && !isLoading && <ApiError message={apiError} />}
            
            {!isLoading && !apiError && chargingHistory.length === 0 && (
              <div className="text-center py-16">
                <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">לא נמצאו רשומות טעינה עבור הרכבים והתאריכים שנבחרו.</p>
              </div>
            )}
            
            {!isLoading && !apiError && chargingHistory.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      {["מס' רכב", "תאריך התחלה", "תאריך סיום", "משך", "סוללה", "אנרגיה (קוט\"ש)", "עלות (₪)", "מיקום", "כתובת", "בעלים", "טלפון", "קמ\"ש", "מרחק"].map(header => (
                        <th key={header} scope="col" className="px-4 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {chargingHistory.map((item, index) => {
                      const car = allCars.find(c => c.car_number === item.car_number);
                      const isWithinRadius = Number(item.distance) <= radius;
                      
                      return (
                        <tr key={index} className="hover:bg-blue-50 transition-colors">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-gray-900">{item.car_number}</div>
                            {car && <div className="text-xs text-gray-500">{car.brand} {car.model}</div>}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{item.datetime_start}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{item.datetime_end}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{item.duration}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-600">{item.percents_start}%</span>
                              <span className="text-gray-400">→</span>
                              <span className="text-gray-900 font-semibold">{item.percents_end}%</span>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
                                +{item.total_percents}%
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {Number(item.total_kw).toFixed(2)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                            ₪{Number(item.total_price).toFixed(2)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{item.location_name}</td>
                          <td className="px-4 py-4 text-sm text-gray-500 max-w-xs truncate" title={item.location_address}>
                            {item.location_address}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{item.owner_name}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            <a href={`tel:${item.owner_phone}`} className="text-blue-600 hover:text-blue-800 hover:underline font-medium">
                              {item.owner_phone}
                            </a>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{item.odo}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-sm ${
                              isWithinRadius 
                                ? 'bg-green-100 text-green-800 border border-green-300' 
                                : 'bg-red-100 text-red-800 border border-red-300'
                            }`}>
                              {isWithinRadius ? '✓' : '✗'} {item.distance} מ'
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ApiError = ({ message }) => (
    <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-md" role="alert">
        <p className="font-bold">שגיאת API</p>
        <p>{message}</p>
    </div>
);
